import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Download } from "lucide-react";
import { CONTACT } from "../../lib/portfolioData";

const LINES = [
  [{ t: "Driving High-Impact" }],
  [{ t: "Revenue", accent: true }, { t: " & Brand" }],
  [{ t: "Partnerships Across" }],
  [{ t: "TV & Digital Media." }],
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-16 md:pt-24 overflow-hidden"
    >
      <div className="absolute top-1/4 -right-40 w-[700px] h-[700px] spotlight pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        {/* Left / text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex items-center gap-3 mb-6 sm:mb-8 max-w-full"
          >
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
            </span>
            <span className="overline min-w-0">Regional Manager · The Hollywood Reporter India</span>
          </motion.div>

          <h1 className="font-display font-medium text-[2rem] sm:text-6xl lg:text-[4.7rem] leading-[1.06] sm:leading-[1.03] tracking-tight">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.05em]">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line.map((seg, j) => (
                    <span key={j} className={seg.accent ? "text-accent italic" : ""}>
                      {seg.t}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 max-w-xl text-base md:text-lg text-[#94a3b8] leading-relaxed"
          >
            13+ years managing multi-market pipelines, scaling strategic accounts,
            and securing premium sponsorships across television and digital media.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#results"
              data-testid="hero-case-studies-btn"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent text-[#04110b] font-medium hover:brightness-110 transition"
            >
              Career Milestones
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#contact"
              data-testid="hero-collaborate-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white hover:border-accent hover:text-accent transition"
            >
              Let&apos;s Collaborate
            </a>
            <a
              href={CONTACT.resume}
              download
              data-testid="hero-resume-btn"
              className="inline-flex items-center gap-2 px-4 py-3.5 text-[#94a3b8] hover:text-white transition link-underline"
            >
              <Download size={16} /> Resume
            </a>
          </motion.div>
        </div>

        {/* Right / portrait */}
        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[280px] sm:w-[340px] lg:w-[400px]"
          >
            <div className="absolute -inset-6 spotlight pointer-events-none" />
            <motion.div style={{ y: imgY }} className="relative">
              <div className="img-arch overflow-hidden border border-white/10 bg-[#0a1220]">
                <motion.img
                  style={{ scale: imgScale }}
                  src="/assets/rohan.jpeg"
                  alt="Rohan Parihar"
                  data-testid="hero-portrait"
                  className="w-full h-[420px] sm:h-[500px] lg:h-[560px] object-cover object-top grayscale-[0.35] hover:grayscale-0 transition-all duration-700"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#050b14] border border-white/10 rounded-2xl px-5 py-4 backdrop-blur-xl">
                <p className="font-display text-3xl text-accent leading-none">13+</p>
                <p className="text-xs text-[#94a3b8] mt-1">Years in Media Sales</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="hidden sm:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#94a3b8]"
      >
        <span className="text-[0.65rem] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px h-8 bg-gradient-to-b from-accent to-transparent"
        />
      </motion.div>
    </section>
  );
};
